import { Component, DestroyRef, HostListener, inject, input, output, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { moduleLinkFor } from '../../constants/screen-registry';
import { ShellNavItem } from '../../models/shell-nav-item';
import { CatalogNavigationService } from '../../services/catalog-navigation.service';
import { ShellNavService } from '../../services/shell-nav.service';
import { iconForNavItem, svgIconForNavItem } from '../../utilities/ui';

@Component({
  selector: 'app-sidebar',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar {
  private readonly catalogNavigation = inject(CatalogNavigationService);
  private clippyClickCount = 0;
  private readonly clippyMessages: string[] = [
    'It looks like things are falling apart... Want some help?',
    "If you're looking for something specific then message Terry!"
  ];
  private clippyTimers: number[] = [];
  private readonly destroyRef = inject(DestroyRef);
  private readonly shellNav = inject(ShellNavService);

  readonly clippyLeaving = signal(false);
  readonly clippyMessage = signal('');
  readonly clippyVisible = signal(false);
  readonly collapseToggled = output<void>();
  readonly collapsed = input(false);

  constructor() {
    this.destroyRef.onDestroy(() => this.clearClippyTimers());
  }

  private clearClippyTimers(): void {
    this.clippyTimers.forEach((timer) => window.clearTimeout(timer));
    this.clippyTimers = [];
  }

  @HostListener('document:keydown.escape')
  hideClippy(): void {
    if (!this.clippyVisible() || this.clippyLeaving()) {
      return;
    }

    this.clearClippyTimers();
    this.clippyLeaving.set(true);
    this.clippyTimers.push(
      window.setTimeout(() => {
        this.clippyVisible.set(false);
        this.clippyLeaving.set(false);
      }, 420)
    );
  }

  iconFor(item: ShellNavItem): string {
    return iconForNavItem(item.screenKey, item.openMode);
  }

  itemsForTeam(team: string): ShellNavItem[] {
    return this.shellNav.itemsForTeam(team);
  }

  onLogoClick(event: MouseEvent): void {
    if (!event.ctrlKey || !event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.clearClippyTimers();
    this.clippyMessage.set(this.clippyMessages[this.clippyClickCount % this.clippyMessages.length]);
    this.clippyClickCount++;
    this.clippyLeaving.set(false);
    if (this.clippyVisible()) {
      this.clippyVisible.set(false);
      this.clippyTimers.push(window.setTimeout(() => this.showClippy(), 0));
      return;
    }

    this.showClippy();
  }

  open(item: ShellNavItem, event: Event): void {
    if (item.screenKind === 'Url' && (item.openMode === 'NewTab' || item.openMode === 'SameWindow')) {
      event.preventDefault();
      this.catalogNavigation.open(item);
    }
  }

  routerLinkFor(item: ShellNavItem): string[] | null {
    if (item.screenKind === 'Url' && (item.openMode === 'NewTab' || item.openMode === 'SameWindow')) {
      return null;
    }

    return moduleLinkFor(item);
  }

  private showClippy(): void {
    this.clippyVisible.set(true);
    this.clippyTimers.push(window.setTimeout(() => this.hideClippy(), 5000));
  }

  svgIconFor(item: ShellNavItem): string | null {
    return svgIconForNavItem(item.screenKey);
  }

  teams(): string[] {
    return this.shellNav.teams();
  }
}
